import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useRebookAppointment } from "@/lib/mutations";
import { useSlotBlocks, useSlotCapacity, usePublicAppSettings, useEffectiveSlotConfig } from "@/lib/queries";
import { isSlotBlocked, dayUnavailableReason } from "@emr/shared/scheduling";
import { DEFAULT_WEEKLY_SCHEDULE } from "@emr/shared/scheduling";
import { todayIST, dateIST } from "@emr/shared/india";
import { toast } from "sonner";
import type { AppointmentRow } from "@/lib/queries";
import { capFor } from "./slotCapacity";

/**
 * Reschedule dialog (2026-10-28 rework).
 *
 * Works for BOTH pending confirmations and confirmed (Booked) appointments:
 *  - The caller picks the TARGET STATUS — "Pending confirmation" (a
 *    slot-bound pending; the row keeps its slot+arrival but does not count
 *    against confirmed capacity) or "Confirmed" (a Booked appointment).
 *    Defaults to the row's current status.
 *  - Standard booking rules apply: working day (weekly schedule), no
 *    whole-day block, no blocked slot, slot key from the target date's
 *    effective config, and capacity — a CONFIRMED target on a full slot is
 *    refused client-side (the API would downgrade it to a slot-bound
 *    pending; the dialog says so instead of doing it silently).
 *  - A slot-less pending may be rescheduled date-only (kept slot-less) —
 *    pick "Pending confirmation" with no slot.
 */
export function RebookDialog({
  open,
  onOpenChange,
  appointment,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  appointment: AppointmentRow | null;
}) {
  const rebook = useRebookAppointment(appointment?.id ?? "");
  const { data: blocksData } = useSlotBlocks();
  const { data: settings } = usePublicAppSettings();
  const blocks = blocksData?.rows ?? [];

  // Target status: "Pending" (pending confirmation) or "Booked" (confirmed).
  // Defaults to the appointment's current status; reset per open/appointment.
  const [newStatus, setNewStatus] = useState<"Pending" | "Booked">("Pending");
  // Default the new date to the appointment's current arrival date (or today
  // if it has no arrival time — e.g. slot-less Pending).
  const initialDate = appointment?.arrivalTime
    ? dateIST(appointment.arrivalTime)
    : todayIST();
  const [newDate, setNewDate] = useState(initialDate);
  const [newSlot, setNewSlot] = useState<string | null>(appointment?.slot ?? null);
  const [newArrival, setNewArrival] = useState(
    appointment?.arrivalTime ? new Date(appointment.arrivalTime).toTimeString().slice(0, 5) : "",
  );

  // Reset state when the dialog opens with a different appointment.
  useEffect(() => {
    if (open && appointment) {
      const d = appointment.arrivalTime
        ? dateIST(appointment.arrivalTime)
        : todayIST();
      setNewDate(d);
      setNewSlot(appointment.slot ?? null);
      setNewArrival(
        appointment.arrivalTime
          ? new Date(appointment.arrivalTime).toTimeString().slice(0, 5)
          : "",
      );
      setNewStatus(appointment.status === "Booked" ? "Booked" : "Pending");
    }
  }, [open, appointment]);

  const { data: capacity } = useSlotCapacity(newDate);
  // The REBOOK TARGET DATE's effective config (not the original date's) —
  // the picker offers whatever slots that date resolves to.
  const { slotList } = useEffectiveSlotConfig(newDate, settings);

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 6);
    return dateIST(d);
  }, []);

  const targetDateObj = new Date(newDate + "T00:00:00");
  // Use the clinic's actual weekly schedule (previously this hardcoded
  // DEFAULT_WEEKLY_SCHEDULE — a custom off-day was invisible here).
  // dayUnavailableReason covers both off days and whole-day blocks, which
  // also protects slot-less pendings (review dates).
  const weeklySchedule = (settings?.weeklySchedule as never) ?? DEFAULT_WEEKLY_SCHEDULE;
  const unavailable = dayUnavailableReason(targetDateObj, weeklySchedule, blocks as any);

  // Per-key capacity: shared lookup (slotCapacity.ts) — Phase 2 slots map
  // first, legacy fields fallback. Same rule as the other dialogs.

  const submit = async () => {
    if (!appointment) return;
    if (!newDate) { toast.error("Please select a date"); return; }
    if (unavailable) {
      toast.error(unavailable.kind === "blocked" ? "Whole day is blocked for this date" : "Selected date is not a working day");
      return;
    }
    // Confirmed target needs a slot (booked_requires_slot_arrival — the API
    // 422s). A pending target may stay slot-less (date-only placeholder).
    if (newStatus === "Booked" && !newSlot) {
      toast.error("Pick a slot for a confirmed appointment");
      return;
    }
    if (newSlot && isSlotBlocked(targetDateObj, newSlot, blocks as any)) {
      toast.error("Selected slot is blocked for this date");
      return;
    }
    // Capacity (standard booking rule): a confirmed target on a full slot is
    // refused here — the API would downgrade it to a slot-bound pending.
    // (Self-exclusion: the row's own current seat doesn't count against it.)
    if (newSlot) {
      const cap = capFor(capacity, newSlot);
      const selfOnTarget =
        appointment.status === "Booked" && appointment.slot === newSlot &&
        appointment.arrivalTime && dateIST(appointment.arrivalTime) === newDate;
      const booked = (cap?.booked ?? 0) - (selfOnTarget ? 1 : 0);
      const max = cap?.max ?? 0;
      if (newStatus === "Booked" && max > 0 && booked >= max) {
        toast.error("That slot is at confirmed capacity — reschedule as pending or pick another slot");
        return;
      }
    }
    // Arrival: explicit time, else the slot def's startTime (the API derives
    // the same default server-side), else date-only (slot-less pending).
    let arrivalIso: string | undefined;
    if (newSlot) {
      const def = slotList.find((s) => s.key === newSlot);
      const time = newArrival || def?.startTime || "09:00";
      arrivalIso = new Date(`${newDate}T${time}:00`).toISOString();
    }
    try {
      await rebook.mutateAsync({
        newDate,
        // null = explicitly slot-less (a date-only pending); the API keeps
        // the current slot only when the field is OMITTED.
        newSlot: newSlot ?? null,
        newArrivalTime: arrivalIso,
        newStatus,
      });
      onOpenChange(false);
    } catch { /* handled by mutation onError */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reschedule appointment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-cream-100 p-3 text-sm dark:bg-dark-700">
            <div className="font-medium">{appointment?.patientName ?? "—"}</div>
            <div className="text-xs text-muted">
              {appointment?.clinicUhid ?? ""} · {appointment?.status ?? ""}
              {appointment?.slot ? ` · ${appointment.slot}` : ""}
            </div>
          </div>
          <div>
            <Label>Reschedule as</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setNewStatus("Pending")}
                className={`rounded-lg border p-3 text-left text-sm transition ${newStatus === "Pending" ? "border-maroon-600 bg-maroon-600/10" : "border-cream-200 hover:border-maroon-300 dark:border-dark-700"}`}
              >
                <div className="font-medium">Pending confirmation</div>
                <div className="text-xs text-muted">Holds the slot without confirming</div>
              </button>
              <button
                type="button"
                onClick={() => setNewStatus("Booked")}
                className={`rounded-lg border p-3 text-left text-sm transition ${newStatus === "Booked" ? "border-maroon-600 bg-maroon-600/10" : "border-cream-200 hover:border-maroon-300 dark:border-dark-700"}`}
              >
                <div className="font-medium">Confirmed</div>
                <div className="text-xs text-muted">A booked appointment</div>
              </button>
            </div>
          </div>
          <div>
            <Label>New date</Label>
            <Input
              type="date"
              value={newDate}
              min={todayIST()}
              max={maxDate}
              onChange={(e) => setNewDate(e.target.value)}
            />
            {newDate && unavailable && (
              <div className="mt-1 text-xs font-medium text-red-600">
                {unavailable.kind === "blocked"
                  ? `Whole day blocked${unavailable.reason ? ` — ${unavailable.reason}` : ""}`
                  : "Not a working day"}
              </div>
            )}
          </div>
          <div>
            <Label>Slot {newStatus === "Pending" && <span className="font-normal text-muted">(optional for pending)</span>}</Label>
            <div className="grid grid-cols-2 gap-2">
              {slotList.map((def) => {
                const blocked = isSlotBlocked(new Date(newDate + "T00:00:00"), def.key, blocks as any);
                const cap = capFor(capacity, def.key);
                const booked = cap?.booked ?? 0;
                const max = cap?.max ?? 0;
                const full = max > 0 && booked >= max;
                return (
                  <button
                    key={def.key}
                    type="button"
                    disabled={blocked}
                    onClick={() => { setNewSlot(def.key); setNewArrival(def.startTime); }}
                    className={`rounded-lg border p-3 text-left text-sm transition ${newSlot === def.key ? "border-maroon-600 bg-maroon-600/10" : "border-cream-200 hover:border-maroon-300 dark:border-dark-700"} ${blocked ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <div className="font-medium">{def.label}</div>
                    <div className="text-xs text-muted">
                      {blocked ? "Blocked" : (
                        <>
                          <span className={full ? "font-semibold text-amber-700" : ""}>
                            {booked}{max > 0 ? `/${max}` : ""}
                          </span>
                          {full && <span className="ml-1 text-amber-700">· full</span>}
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
              </div>
              {newStatus === "Pending" && !newSlot && (
                <div className="mt-1 text-xs text-muted">
                  No slot picked — the pending stays slot-less (date-only, like review-flow pendings).
                </div>
              )}
          </div>
          {newSlot && (
            <div>
              <Label>Arrival time</Label>
              <Input type="time" value={newArrival} onChange={(e) => setNewArrival(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!newDate || !!unavailable || rebook.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}